/**
 * Keep in sync with `src/utils/productVariants.ts` (API).
 * Collapses Sony Style-tile scrape junk into the two PDP options.
 */

/**
 * What a variant row represents relative to its parent product:
 * - kit:           parent + lens(es) sold as one SKU (e.g. "Z6III 24-70mm f/4 Lens Kit")
 * - bundle:        parent + non-lens extras (grip, adapter, memory, …)
 * - configuration: alternate storefront configuration (e.g. "Body Only")
 * - style:         cosmetic option (color, finish)
 */
export type ProductVariantKind = 'kit' | 'bundle' | 'configuration' | 'style';

export type VariantLike = {
  sku?: string;
  upc?: string;
  ean?: string;
  asin?: string;
  mpn?: string;
  optionName?: string;
  optionValue?: string;
  title?: string;
  name?: string;
  source?: string;
  kind?: ProductVariantKind;
};

export type NormalizedProductVariant = {
  sku: string;
  upc?: string;
  ean?: string;
  asin?: string;
  mpn?: string;
  optionName: string;
  optionValue: string;
  title?: string;
  source?: string;
  kind?: ProductVariantKind;
};

const KIT_RE = /body\s*\+\s*[\d.]+(?:\s*-\s*[\d.]+)?\s*mm(?:\s+zoom)?(?:\s+lens)?/gi;
const BODY_ONLY_RE = /body\s+only/i;

/** Classify a variant label into its ProductVariantKind. */
export function classifyVariantKind(label: string): ProductVariantKind {
  const t = collapseWs(label);
  if (!t) return 'configuration';
  if (BODY_ONLY_RE.test(t)) return 'configuration';
  if (/\bkit\b/i.test(t) || /body\s*\+.*mm/i.test(t) || /\bwith\b.*\d+\s*-?\s*\d*\s*mm/i.test(t)) {
    return 'kit';
  }
  if (/\bbundle\b|\bcombo\b|\bwith\b/i.test(t)) return 'bundle';
  if (/\b(black|silver|white|graphite|colou?r)\b/i.test(t)) return 'style';
  return 'configuration';
}

export function collapseWs(s: string): string {
  return String(s || '').replace(/\s+/g, ' ').trim();
}

/**
 * Sibling SKU/MPN that is the row's own Amazon ASIN is not a manufacturer part
 * number. With no `asin` on the row, the SKU is still copied to `mpn` as before.
 * Keep in sync with `src/utils/productVariants.ts`.
 */
function mpnUnlessAsin(asin: string | undefined, mpnOrSku: string): string | undefined {
  const value = mpnOrSku.trim();
  if (!value) return undefined;
  if (asin && value.toUpperCase().startsWith(asin.toUpperCase())) return undefined;
  return value;
}

export function stripStylePrefix(s: string): string {
  return collapseWs(s.replace(/^style\s*:\s*/i, ''));
}

function prettyStyleLabel(s: string): string {
  const t = collapseWs(s);
  if (/^body\s+only$/i.test(t)) return 'Body Only';
  return t
    .replace(/\bbody\b/i, 'Body')
    .replace(/\bzoom\s+lens\b/i, 'Zoom Lens')
    .replace(/\blens\b/i, 'Lens');
}

/** Distinct Sony Style options mentioned in a tile label. */
export function extractStyleOptionLabels(raw: string): string[] {
  const text = stripStylePrefix(raw);
  if (!text) return [];
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const m of text.match(KIT_RE) || []) {
    const label = prettyStyleLabel(m);
    const key = label.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      labels.push(label);
    }
  }
  if (BODY_ONLY_RE.test(text) && !seen.has('body only')) {
    labels.push('Body Only');
  }
  return labels;
}

function rowLabel(v: VariantLike): string {
  return String(v.optionValue || v.name || v.title || '');
}

/** Label for chips / lists when stored rows mix `optionValue`, `name`, and `title`. */
export function variantDisplayLabel(v: VariantLike): string {
  return collapseWs(String(v.optionValue || v.name || v.title || v.sku || v.mpn || v.upc || ''));
}

const COLOR_LEAF_RE = /^(colou?r|finish)$/i;

function isColorLeafKey(key: string): boolean {
  const leaf = (key.split('.').pop() || key).trim();
  return COLOR_LEAF_RE.test(leaf);
}

/** First Color/Colour/Finish spec string from nested or flattened specifications. */
export function extractColorSpecRaw(specs: unknown): string {
  if (!specs) return '';
  if (typeof specs === 'string') return String(specs);
  if (typeof specs !== 'object') return '';
  const found: string[] = [];
  const walk = (obj: unknown): void => {
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
      obj.forEach(walk);
      return;
    }
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (v == null) continue;
      if (typeof v === 'string' && isColorLeafKey(k) && collapseWs(v)) {
        found.push(v);
      } else if (typeof v === 'object') {
        walk(v);
      }
    }
  };
  walk(specs);
  return found[0] || '';
}

/**
 * Sony (and similar) color specs look like `Silver #212028 | Black#b6b5cf | Silver`.
 * Returns distinct human labels, dropping hex swatch codes.
 */
export function parseColorSpecOptions(raw: string): string[] {
  const stripped = collapseWs(
    String(raw || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/#(?:[0-9a-fA-F]{3,8})\b/g, ' '),
  );
  if (!stripped) return [];
  const parts = stripped
    .split(/\s*(?:\||\/|,|•|;|\band\b)\s*/i)
    .map((p) => collapseWs(p))
    .filter((p) => p.length > 0 && p.length <= 40 && !/^\d+$/.test(p));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/**
 * Collapse scrape stubs and, when those are empty, recover color options from specs.
 */
export function hydrateProductVariants(raw: unknown, specs?: unknown): NormalizedProductVariant[] {
  const normalized = normalizeProductVariants(raw);
  if (normalized.length > 0) return normalized;
  const colors = parseColorSpecOptions(extractColorSpecRaw(specs));
  if (colors.length < 2) return [];
  return colors.map((label) => ({
    sku: '',
    optionName: 'Color',
    optionValue: label,
    title: label,
    kind: 'style' as const,
    source: 'spec-color',
  }));
}

export function normalizeProductVariants(raw: unknown): NormalizedProductVariant[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const rows = raw.filter((v) => v && typeof v === 'object') as VariantLike[];

  const styleLabels: string[] = [];
  const seenStyle = new Set<string>();
  for (const v of rows) {
    for (const label of extractStyleOptionLabels(rowLabel(v))) {
      const key = label.toLowerCase();
      if (seenStyle.has(key)) continue;
      seenStyle.add(key);
      styleLabels.push(label);
    }
  }

  if (styleLabels.length >= 2) {
    return styleLabels.map((label) => {
      const match = rows.find((v) => stripStylePrefix(rowLabel(v)).toLowerCase() === label.toLowerCase());
      const sku = String(match?.sku || match?.mpn || '').trim();
      const out: NormalizedProductVariant = {
        sku,
        optionName: 'Style',
        optionValue: label,
        title: label,
        source: match?.source || 'sony-pdp',
        kind: match?.kind || classifyVariantKind(label),
      };
      if (match?.upc) out.upc = String(match.upc);
      if (match?.ean) out.ean = String(match.ean);
      if (match?.asin) out.asin = String(match.asin).trim();
      const styleMpn = mpnUnlessAsin(out.asin, String(match?.mpn || sku || ''));
      if (styleMpn) out.mpn = styleMpn;
      return out;
    });
  }

  const out: NormalizedProductVariant[] = [];
  const seen = new Set<string>();
  for (const v of rows) {
    const optionValue = stripStylePrefix(rowLabel(v));
    const sku = String(v.sku || v.mpn || '').trim();
    if (!optionValue && !sku && !v.upc) continue;
    if (extractStyleOptionLabels(optionValue).length > 1) continue;
    const key = `${sku}|${optionValue.toLowerCase()}|${v.upc || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const row: NormalizedProductVariant = {
      sku,
      optionName: collapseWs(String(v.optionName || 'Option')) || 'Option',
      optionValue: optionValue || sku || String(v.upc || ''),
      kind: v.kind || classifyVariantKind(optionValue || String(v.title || v.name || '')),
    };
    if (v.upc) row.upc = String(v.upc);
    if (v.ean) row.ean = String(v.ean);
    if (v.asin) row.asin = String(v.asin).trim();
    const rowMpn = mpnUnlessAsin(row.asin, String(v.mpn || sku || ''));
    if (rowMpn) row.mpn = rowMpn;
    if (v.title || v.name) row.title = String(v.title || v.name);
    if (v.source) row.source = String(v.source);
    out.push(row);
  }
  return out;
}

function normVariantId(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

/** Fill empty sibling upc/ean from this product or linked catalog rows. */
export function applyCatalogBarcodesToVariants(
  variants: NormalizedProductVariant[],
  catalog: Array<{ sku?: string | null; upc?: string | null; ean?: string | null }>,
): NormalizedProductVariant[] {
  const bySku = new Map<string, { upc?: string; ean?: string }>();
  for (const row of catalog) {
    const sku = String(row.sku || '').trim().toLowerCase();
    if (!sku) continue;
    const upc = String(row.upc || '').trim() || undefined;
    const ean = String(row.ean || '').trim() || undefined;
    if (!upc && !ean) continue;
    bySku.set(sku, { ...(upc ? { upc } : {}), ...(ean ? { ean } : {}) });
  }
  return variants.map((variant) => {
    const hit = bySku.get(String(variant.sku || '').trim().toLowerCase());
    if (!hit) return variant;
    return {
      ...variant,
      ...(variant.upc ? {} : hit.upc ? { upc: hit.upc } : {}),
      ...(variant.ean ? {} : hit.ean ? { ean: hit.ean } : {}),
    };
  });
}

/**
 * Which variant row is "this product". SKU/MPN wins over barcode so kit
 * siblings that reuse the keeper UPC (SmallRig B0… rows) are not highlighted
 * as the self row.
 */
export function isSelfVariantRow(
  product: { sku?: string; mpn?: string; upc?: string; ean?: string },
  variant: { sku?: string; mpn?: string; upc?: string; ean?: string }
): boolean {
  const productSku = normVariantId(product.sku) || normVariantId(product.mpn);
  const variantSku = normVariantId(variant.sku) || normVariantId(variant.mpn);
  if (productSku && variantSku) return productSku === variantSku;

  const productUpc = normVariantId(product.upc);
  const productEan = normVariantId(product.ean);
  const variantUpc = normVariantId(variant.upc);
  const variantEan = normVariantId(variant.ean);
  if (productUpc && (variantUpc === productUpc || variantEan === productUpc)) return true;
  if (productEan && (variantEan === productEan || variantUpc === productEan)) return true;
  return false;
}

export type FamilyCatalogProduct = {
  sku?: string | null;
  mpn?: string | null;
  upc?: string | null;
  ean?: string | null;
  name?: string | null;
  model?: string | null;
  parentSku?: string | null;
};

function configurationOptionLabel(product: FamilyCatalogProduct, family: FamilyCatalogProduct[]): string {
  const name = collapseWs(product.name || '');
  const paren = name.match(/\(([^)]+)\)\s*$/);
  if (paren?.[1]) return collapseWs(paren[1]);
  const sku = normVariantId(product.sku);
  const isParent = Boolean(sku) && family.some((row) => normVariantId(row.parentSku) === sku);
  if (isParent || /body\s+only/i.test(name)) return 'Body Only';
  return collapseWs(product.model || name || String(product.sku || ''));
}

function numericSku(sku: string): number | null {
  return /^\d+$/.test(sku) ? Number(sku) : null;
}

/**
 * Variants stored on a kit list the body and the other kits, not itself.
 * The linked catalog already has every configuration, so the option list
 * includes this product and any linked SKU the record left out.
 */
export function completeFamilyVariantRows(
  variants: NormalizedProductVariant[],
  product: FamilyCatalogProduct,
  linked: FamilyCatalogProduct[],
): NormalizedProductVariant[] {
  const family = [product, ...linked];
  const present = new Set(
    variants.map((row) => normVariantId(row.sku) || normVariantId(row.mpn)).filter(Boolean),
  );
  const optionName = variants.find((row) => row.optionName)?.optionName || 'Configuration';
  const missing: NormalizedProductVariant[] = [];
  for (const member of family) {
    const sku = String(member.sku || member.mpn || '').trim();
    const key = normVariantId(sku);
    if (!key || present.has(key)) continue;
    const optionValue = configurationOptionLabel(member, family);
    const row: NormalizedProductVariant = {
      sku,
      optionName,
      optionValue,
      kind: classifyVariantKind(optionValue),
    };
    if (member.upc) row.upc = String(member.upc);
    if (member.ean) row.ean = String(member.ean);
    if (member.mpn || sku) row.mpn = String(member.mpn || sku);
    if (member.name) row.title = String(member.name);
    missing.push(row);
    present.add(key);
  }
  if (missing.length === 0) return variants;

  const rows = [...variants];
  for (const row of missing) {
    const key = numericSku(row.sku);
    if (key == null) {
      rows.push(row);
      continue;
    }
    const index = rows.findIndex((existing) => {
      const existingKey = numericSku(existing.sku);
      return existingKey != null && existingKey > key;
    });
    if (index === -1) rows.push(row);
    else rows.splice(index, 0, row);
  }
  return rows;
}
